import { useEffect, useId, useState, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { BarChart3, CreditCard, Layers } from 'lucide-react';
import { Button, useToast } from '@vd/ui';
import type { BillingInterval, BillingPlansResponse, SelfServicePlan } from '@vd/types';
import { useCurrentCompany } from '../../../features/auth/AuthProvider';
import {
  billingQueryKey,
  fetchSubscription,
  minSeatsFor,
} from '../../../features/billing/useBillingSummary';
import { ActiveAddons, GetMore } from '../../../features/billing/UsageAddons';
import { BillingPlans } from '../../../features/billing/BillingPlans';
import { BillingUsage } from '../../../features/billing/BillingUsage';
import { BillingDialog } from '../../../features/billing/BillingDialog';
import { EnterpriseInquiryForm } from '../../../features/billing/EnterpriseInquiryForm';
import {
  PLAN_LABELS,
  formatBillingDate,
  heldPrice,
} from '../../../features/billing/billingDisplay';
import { get, post } from '../../../lib/apiClient';
import styles from '../../../features/billing/Billing.module.css';
export { heldPrice, priceLabels, stripeAmount } from '../../../features/billing/billingDisplay';

export function BillingTab() {
  const company = useCurrentCompany();
  return company ? (
    <BillingWorkspace key={company.id} company={company} />
  ) : (
    <p>Select a company to view billing.</p>
  );
}
function BillingWorkspace({
  company,
}: {
  company: NonNullable<ReturnType<typeof useCurrentCompany>>;
}) {
  const companyId = company.id;
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'usage' ? 'usage' : 'plans';
  const [interval, setInterval] = useState<BillingInterval>('annual');
  const [busy, setBusy] = useState(false);
  const [contact, setContact] = useState(false);
  const [free, setFree] = useState(false);
  const [widgetOpen, setWidgetOpen] = useState(false);
  const [widgetFocus, setWidgetFocus] = useState(false);
  const id = useId();
  const isOwner = company.role === 'owner';
  const subscription = useQuery({
    queryKey: billingQueryKey(companyId),
    queryFn: () => fetchSubscription(companyId),
  });
  const metadata = useQuery({
    queryKey: ['billing-plans', companyId],
    queryFn: () =>
      get<BillingPlansResponse>(`/v1/billing/plans?companyId=${encodeURIComponent(companyId)}`),
  });
  const selectView = (next: 'plans' | 'usage') => {
    if (view === next) return;
    setParams((old) => {
      const copy = new URLSearchParams(old);
      copy.set('view', next);
      return copy;
    });
  };
  useEffect(() => {
    if (!widgetFocus || view !== 'plans') return;
    const summary = document.querySelector<HTMLElement>('#billing-widget-capacity > summary');
    summary?.focus();
    summary?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    setWidgetFocus(false);
  }, [widgetFocus, view]);
  const tabKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const next =
      event.key === 'Home'
        ? 'plans'
        : event.key === 'End'
          ? 'usage'
          : ['ArrowLeft', 'ArrowRight'].includes(event.key)
            ? view === 'plans'
              ? 'usage'
              : 'plans'
            : null;
    if (!next) return;
    event.preventDefault();
    selectView(next);
    document.getElementById(`${id}-${next}-tab`)?.focus();
  };
  const changed = () => {
    void subscription.refetch();
    void metadata.refetch();
  };
  const sub = subscription.data;
  const configured = sub?.billingConfigured !== false;
  const redirect = async (path: string, body: object) => {
    if (!isOwner || busy || !sub) return;
    if (!configured) {
      toast.push({
        variant: 'info',
        title: 'Billing unavailable',
        description: 'Online billing is not configured in this environment.',
      });
      return;
    }
    setBusy(true);
    try {
      window.location.assign((await post<{ url: string }>(path, body)).url);
    } catch (error) {
      toast.push({
        variant: 'danger',
        title: 'Could not open billing',
        description: (error as Error).message,
      });
      setBusy(false);
    }
  };
  const manage = () => {
    void redirect('/v1/billing/portal', { companyId });
  };
  const choose = (plan: SelfServicePlan) => {
    if (!isOwner || busy || !sub) return;
    if (sub.plan === 'enterprise') {
      setContact(true);
      return;
    }
    if (sub.plan !== 'free') {
      manage();
      return;
    }
    // Changes to a paid plan must use the existing subscription's portal.
    const seats =
      plan === 'personal' ? 1 : Math.max(sub.seatsPurchased, minSeatsFor(plan, sub.catalog));
    void redirect('/v1/billing/checkout', { companyId, plan, interval, seats });
  };
  if (subscription.isLoading) return <p role="status">Loading billing…</p>;
  if (subscription.error || !sub)
    return (
      <div className={styles.notice} role="alert">
        Could not load billing.{' '}
        <button onClick={() => void subscription.refetch()}>Retry billing</button>
      </div>
    );
  const paid = ['personal', 'team', 'business'].includes(sub.plan);
  const price = heldPrice(sub.currentPrice, sub.plan === 'team' || sub.plan === 'business');
  const widget = () => {
    selectView('plans');
    setWidgetOpen(true);
    setWidgetFocus(true);
  };
  return (
    <div className={styles.billing}>
      <header className={styles.heading}>
        <div>
          <h1>
            <span className={`${styles.iconTile} ${styles.blue}`}>
              <CreditCard aria-hidden="true" size={20} />
            </span>
            Billing
          </h1>
          <p className={styles.muted}>
            Understand your plan, capacity and usage in {company.name}.
          </p>
        </div>
      </header>
      {!configured && (
        <div className={`${styles.notice} ${styles.warning}`} role="status">
          Online billing is unavailable in this environment. Subscription and usage data still
          reflect this workspace.
        </div>
      )}
      {['past_due', 'unpaid'].includes(sub.subscriptionStatus) && (
        <div className={`${styles.notice} ${styles.error}`} role="alert">
          Payment past due. Update your payment method to keep your subscription active.{' '}
          {isOwner && (
            <Button onClick={manage} disabled={busy}>
              Update payment method
            </Button>
          )}
        </div>
      )}
      <section className={styles.subscription} aria-label="Current subscription">
        <div className={styles.spread}>
          <div>
            <div className={styles.eyebrow}>Current subscription</div>
            <h2>
              {PLAN_LABELS[sub.plan]} plan{' '}
              <span className={styles.badge}>{sub.subscriptionStatus.replaceAll('_', ' ')}</span>
            </h2>
            <p className={styles.muted}>
              {sub.seatsPurchased} seat{sub.seatsPurchased === 1 ? '' : 's'}
              {price}
              {sub.currentPeriodEnd &&
                ` · ${sub.cancelAtPeriodEnd || sub.subscriptionStatus === 'canceled' ? 'ends' : ['active', 'trialing'].includes(sub.subscriptionStatus) ? 'renews' : 'period end'} ${formatBillingDate(sub.currentPeriodEnd)}`}
            </p>
            {price && (
              <p className={styles.footnote}>
                Subscription unit price before discounts and taxes. Stripe shows your final invoice
                total.
              </p>
            )}
            {sub.plan === 'enterprise' && (
              <p className={styles.muted}>Your contract controls pricing and allowances.</p>
            )}
            <p className={styles.muted}>
              {isOwner
                ? 'You control this workspace’s subscription.'
                : 'Only the workspace owner can change billing.'}
            </p>
          </div>
          {isOwner &&
            (paid ? (
              <Button variant="secondary" disabled={busy} onClick={manage}>
                Manage subscription
              </Button>
            ) : sub.plan === 'enterprise' ? (
              <Button variant="secondary" onClick={() => setContact(true)}>
                Contact us
              </Button>
            ) : null)}
        </div>
      </section>
      {(params.get('checkout') === 'success' || params.get('addon') === 'success') && (
        <div className={`${styles.notice} ${styles.success}`} role="status">
          Payment complete. Changes appear when Stripe confirms them, usually within a few seconds.{' '}
          <button className={styles.textLink} onClick={changed}>
            Refresh subscription
          </button>
        </div>
      )}
      <div className={styles.tabs} role="tablist" aria-label="Billing views">
        {(['plans', 'usage'] as const).map((tab) => (
          <button
            key={tab}
            id={`${id}-${tab}-tab`}
            role="tab"
            aria-selected={view === tab}
            aria-controls={`${id}-${tab}-panel`}
            tabIndex={view === tab ? 0 : -1}
            onKeyDown={tabKey}
            onClick={() => selectView(tab)}
            className={styles.tab}
          >
            {tab === 'plans' ? (
              <Layers aria-hidden="true" size={16} />
            ) : (
              <BarChart3 aria-hidden="true" size={16} />
            )}{' '}
            {tab === 'plans' ? 'Plans' : 'Usage'}
          </button>
        ))}
      </div>
      <section
        id={`${id}-plans-panel`}
        role="tabpanel"
        aria-labelledby={`${id}-plans-tab`}
        hidden={view !== 'plans'}
        className={styles.panel}
      >
        <BillingPlans
          sub={sub}
          details={metadata.data}
          loading={metadata.isLoading}
          error={!!metadata.error}
          retry={() => void metadata.refetch()}
          interval={interval}
          setInterval={setInterval}
          isOwner={isOwner}
          busy={busy}
          companyId={companyId}
          actions={{ choose, manage, contact: () => setContact(true), free: () => setFree(true) }}
          widgetOpen={widgetOpen}
          setWidgetOpen={setWidgetOpen}
          changed={changed}
        />
      </section>
      <section
        id={`${id}-usage-panel`}
        role="tabpanel"
        aria-labelledby={`${id}-usage-tab`}
        hidden={view !== 'usage'}
        className={styles.panel}
      >
        <BillingUsage
          sub={sub}
          details={metadata.data}
          onWidget={widget}
          getMore={(meter) => (
            <GetMore
              meter={meter}
              addons={sub.addons}
              plan={sub.plan}
              isOwner={isOwner}
              contactUrl={sub.catalog?.contactUrl ?? 'https://toado.dev/contact'}
              companyId={companyId}
              billingConfigured={configured}
              businessOnSale={!!sub.catalog?.plans.business}
              onPlan={choose}
            />
          )}
        />
        {sub.addons && (
          <div className={styles.usageSection}>
            <ActiveAddons
              addons={sub.addons}
              isOwner={isOwner}
              companyId={companyId}
              billingConfigured={configured}
              videoHistory={sub.recording?.videoHistory}
              onChanged={changed}
            />
          </div>
        )}
      </section>
      {contact && (
        <EnterpriseInquiryForm
          companyId={companyId}
          companyName={company.name}
          onClose={() => setContact(false)}
        />
      )}
      {free && (
        <BillingDialog titleId={`${id}-free-title`} onClose={() => setFree(false)}>
          <h2 id={`${id}-free-title`}>
            {sub.cancelAtPeriodEnd ? 'Review scheduled cancellation' : 'Switch to Free?'}
          </h2>
          <p className={styles.muted}>
            Free defaults to 40 internal tickets per calendar month and lower recording, storage and
            retention allowances. Workspace overrides may still apply. Existing add-on subscriptions
            may need to be stopped separately.
          </p>
          <p className={styles.muted}>
            Continue to Stripe to review cancellation and its effective date. Your workspace stays
            on its current plan until Stripe confirms the change.
          </p>
          {sub.cancelAtPeriodEnd && sub.currentPeriodEnd && (
            <p>Current subscription ends {formatBillingDate(sub.currentPeriodEnd)}.</p>
          )}
          <div className={styles.dialogActions}>
            <Button autoFocus variant="secondary" onClick={() => setFree(false)}>
              Keep current plan
            </Button>
            <Button
              disabled={busy}
              onClick={() => {
                setFree(false);
                manage();
              }}
            >
              Continue to Stripe
            </Button>
          </div>
        </BillingDialog>
      )}
    </div>
  );
}
